import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coins, Trophy, Gift, Wallet, Store, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 10000;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export const LiveBoard = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(false);
  const timer = useRef(null);
  const ctrl = useRef(null);

  const load = async () => {
    ctrl.current?.abort();
    ctrl.current = new AbortController();
    try {
      const r = await fetch(`${BACKEND}/api/public/live-board`, { signal: ctrl.current.signal });
      const d = await r.json();
      setData(d); setPulse(true); setTimeout(() => setPulse(false), 700);
    } catch (e) { if (e?.name !== "AbortError") console.warn("live-board refresh failed", e); /* keep last good data */ }
  };
  useEffect(() => {
    load();
    timer.current = setInterval(load, REFRESH_MS);
    return () => { clearInterval(timer.current); ctrl.current?.abort(); };
  }, []);

  const s = data?.stats;
  const stats = [
    { icon: Coins, label: "Total cashback given", value: s?.total_cashback_display ?? "₹—", tone: "emerald" },
    { icon: Wallet, label: "Cashback today", value: s?.cashback_today_display ?? "₹—", tone: "sky" },
    { icon: Trophy, label: "Lucky-draw winners", value: s?.total_winners ?? "—", tone: "amber" },
    { icon: Gift, label: "Prizes paid out", value: s?.total_prize_display ?? "₹—", tone: "fuchsia" },
  ];
  const tones = {
    emerald: "from-emerald-500/15 text-emerald-400 border-emerald-500/25",
    sky: "from-sky-500/15 text-sky-400 border-sky-500/25",
    amber: "from-amber-500/15 text-amber-400 border-amber-500/25",
    fuchsia: "from-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/25",
  };

  return (
    <div data-testid="live-board" className="space-y-6">
      <div className="flex items-center gap-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 ${pulse ? "animate-ping" : ""} opacity-75`} />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Live · updates every 10s</p>
      </div>

      <div className={`grid gap-3 ${compact ? "grid-cols-2" : "grid-cols-2 lg:grid-cols-4"}`}>
        {stats.map((st, i) => {
          const I = st.icon;
          return (
            <div key={i} data-testid={`live-stat-${i}`} className={`rounded-2xl border bg-gradient-to-br to-slate-900/40 p-4 ${tones[st.tone]}`}>
              <I className="h-5 w-5" />
              <p className="mt-3 font-heading text-2xl font-extrabold text-white">{st.value}</p>
              <p className="mt-0.5 text-xs text-slate-400">{st.label}</p>
            </div>
          );
        })}
      </div>

      <div className={`grid gap-4 ${compact ? "" : "lg:grid-cols-2"}`}>
        <Feed title="Recent cashback" icon={Coins} testid="live-cashback-feed" count={(data?.recent_cashback || []).length} empty="Cashback rewards will appear here.">
          <AnimatePresence initial={false}>
            {(data?.recent_cashback || []).map((c, i) => (
              <Row key={`${c.name}-${c.at}-${i}`} testid="live-cashback-row"
                left={<><span className="font-semibold text-white">{c.name}</span> earned <span className="font-semibold text-emerald-400">{c.amount_display}</span></>}
                sub={<><Store className="mr-1 inline h-3 w-3" />{c.merchant}</>}
                right={timeAgo(c.at)} accent="emerald" />
            ))}
          </AnimatePresence>
        </Feed>

        <Feed title="Recent lucky-draw winners" icon={Trophy} testid="live-winners-feed" count={(data?.recent_winners || []).length} empty="Winners will be shown here after the next draw.">
          <AnimatePresence initial={false}>
            {(data?.recent_winners || []).map((w, i) => (
              <Row key={`${w.ticketCode}-${i}`} testid="live-winner-row"
                left={<><span className="font-semibold text-white">{w.winner_name}</span> won <span className="font-semibold text-amber-400">{w.prize_display}</span></>}
                sub={<>🎟️ {w.ticketCode} · {w.prize_label} · {w.draw_name}</>}
                right={timeAgo(w.draw_date)} accent="amber" />
            ))}
          </AnimatePresence>
        </Feed>
      </div>

      <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-600">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-500/70" /> Names & ticket codes are masked for privacy. Winners are picked securely on our servers.
      </p>
    </div>
  );
};

function Feed({ title, icon: Icon, children, testid, empty, count = 0 }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
      <div className="mb-3 flex items-center gap-2"><Icon className="h-4 w-4 text-slate-300" /><p className="font-heading text-sm font-bold">{title}</p></div>
      <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
        {children}
        {count === 0 && <p className="py-6 text-center text-xs text-slate-600">{empty}</p>}
      </div>
    </div>
  );
}

function Row({ left, sub, right, accent, testid }) {
  const bar = accent === "amber" ? "bg-amber-500/60" : "bg-emerald-500/60";
  return (
    <motion.div data-testid={testid}
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="flex items-start gap-3 rounded-xl border border-slate-800/70 bg-slate-950/40 p-3">
      <span className={`mt-1 h-8 w-1 flex-shrink-0 rounded-full ${bar}`} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-slate-300">{left}</p>
        <p className="mt-0.5 truncate text-xs text-slate-500">{sub}</p>
      </div>
      <span className="flex-shrink-0 font-mono text-[10px] text-slate-600">{right}</span>
    </motion.div>
  );
}
